import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
  useContext,
  useEffect,
} from "react";
import { IFilterParams } from "ag-grid-community";
import { IFilterReactComp } from "ag-grid-react";
import { List } from "@mui/material";
import {
  COLLECTION_COLUMN_STATE,
  BREAK_UP_CLOUD_CONTACT_CENTER_COLUMN,
  BREAK_UP_AOV_CLOUD_CONTACT_CENTER_COLUMN,
  AOV_COLLECTION_COLUMN_STATE,
} from "constant";
import CheckboxTree from "react-checkbox-tree";
import {
  CheckBox,
  CheckBoxOutlineBlank,
  ChevronRight,
  ExpandMore,
  IndeterminateCheckBox,
} from "@mui/icons-material";
import { CollectionRevenueContext } from "../cellRenderer/CollectionRevenueCellRenderer";
interface CheckboxItem {
  label: string;
  value: string;
  children?: CheckboxItem[];
}

const CollectionRevenueCellRenderer = forwardRef<
  IFilterReactComp,
  Partial<IFilterParams> & {
    collection: string[];
    type: string;
    stateKey?: string;
  }
>(({ api, collection, column, type = "arr", stateKey }, ref) => {
  useImperativeHandle(ref, () => ({
    isFilterActive: () => false,
    getModel: () => undefined,
    doesFilterPass: () => true,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setModel: () => {},
  }));

  const { setCollectionColumns, setAOVCollectionColumns } = useContext(CollectionRevenueContext);

  const [checked, setChecked] = useState<string[]>([]);
  const [expanded, setExpand] = useState<string[]>([
    "Select All",
    "Collab",
    "Non-Collab",
    type === "arr" ? "Collab%-%invoiceArrContactCenterCloud" : "Collab%-%aovContactCenterCloud",
  ]);

  const splitedCols = [
    ...(type === "arr"
      ? BREAK_UP_CLOUD_CONTACT_CENTER_COLUMN
      : BREAK_UP_AOV_CLOUD_CONTACT_CENTER_COLUMN),
  ];
  const collectionStateKey =
    stateKey ?? (type === "arr" ? COLLECTION_COLUMN_STATE : AOV_COLLECTION_COLUMN_STATE);

  const columns = useMemo(() => {
    return collection.map((x) => api!.getColumnDef(x));
  }, [collection]);

  const handledColumns: CheckboxItem[] = [];

  columns?.forEach((col) => {
    if (col?.colId?.includes("Non")) {
      handledColumns.push({
        label: col?.headerName ?? "",
        value: col?.colId ? "Non-Collab%-%" + col?.colId : "",
      });
    } else {
      // colId: invoiceArrContactCenterCloud --> BREAK_UP_ClOUND_CONTACT_CENTER_COLUMN
      if (
        type === "arr"
          ? col?.colId === "invoiceArrContactCenterCloud"
          : col?.colId === "aovContactCenterCloud"
      ) {
        handledColumns.push({
          label: col?.headerName ?? "",
          value: col?.colId ? "Collab%-%" + col?.colId : "",
          children: (type === "arr"
            ? BREAK_UP_CLOUD_CONTACT_CENTER_COLUMN
            : BREAK_UP_AOV_CLOUD_CONTACT_CENTER_COLUMN
          )
            .map((x) => api!.getColumnDef(x))
            .map((c) => {
              return {
                label: c?.headerName ?? "",
                value: c?.colId ? "Collab%-%Cloud%-%" + c.colId : "",
              };
            }),
        });
      }
      if (
        (type === "arr"
          ? col?.colId !== "invoiceArrContactCenterCloud"
          : col?.colId !== "aovContactCenterCloud") &&
        !splitedCols.includes(col?.colId as string)
      )
        handledColumns.push({
          label: col?.headerName ?? "",
          value: col?.colId ? "Collab%-%" + col?.colId : "",
        });
    }
  });

  // console.log(handledColumns);

  const newGroup = [
    {
      label: "Select All",
      value: "Select All",
      children: [
        {
          label: "Collab",
          value: "Collab",
          children: handledColumns.filter((c: CheckboxItem) => !c.value.includes("Non")),
        },
        {
          label: "Non-Collab",
          value: "Non-Collab",
          children: handledColumns.filter((c: CheckboxItem) => c.value.includes("Non")),
        },
      ],
    },
  ];
  // if (type === "arr") {
  //   newGroup?.[0]?.children.push({
  //     label: "Non-Collab",
  //     value: "Non-Collab",
  //     children: handledColumns.filter((c: CheckboxItem) =>
  //       c.value.includes("Non")
  //     ),
  //   });
  // }

  const handleSplit = (col: string) => {
    const isSplitCloud = (
      type === "arr"
        ? BREAK_UP_CLOUD_CONTACT_CENTER_COLUMN
        : BREAK_UP_AOV_CLOUD_CONTACT_CENTER_COLUMN
    ).includes(col);
    if (isSplitCloud) {
      return "Collab%-%Cloud%-%";
    } else {
      return "Collab%-%";
    }
  };

  useEffect(() => {
    const checkedColumns = localStorage.getItem(collectionStateKey);
    if (checkedColumns) {
      try {
        const parsedChecked = JSON.parse(checkedColumns);
        if (Array.isArray(parsedChecked) && parsedChecked?.length) {
          const validChecked = parsedChecked.filter((col: string) => collection.includes(col));
          setChecked(
            (validChecked.length ? validChecked : collection)?.map(
              (col: string) => (col?.includes("Non") ? "Non-Collab%-%" : handleSplit(col)) + col
            )
          );
        } else {
          setChecked([]);
        }
      } catch (_e) {
        setChecked(
          collection.map(
            (col: string) => (col?.includes("Non") ? "Non-Collab%-%" : handleSplit(col)) + col
          )
        );
      }
    } else {
      setChecked(
        collection.map(
          (col: string) => (col?.includes("Non") ? "Non-Collab%-%" : handleSplit(col)) + col
        )
      );
    }
  }, []);

  const handleCheck = (checked: string[]) => {
    if (column!.isSortAscending() || column!.isSortDescending() || column!.isFilterActive()) {
      api!.purgeServerSideCache();
    }

    if (
      checked.length === 1 &&
      checked?.[0] ===
        (type === "arr"
          ? "Collab%-%invoiceArrContactCenterCloud"
          : "Collab%-%aovContactCenterCloud")
    ) {
      setChecked([]);
    } else {
      setChecked(checked);
    }
    const initialCols = checked?.map((col) =>
      col?.split("%-%")?.length === 2 ? col?.split("%-%")?.[1] : col?.split("%-%")?.[2]
    );
    if (setCollectionColumns && type === "arr") {
      if (initialCols?.length) {
        setCollectionColumns(initialCols);
        localStorage.setItem(collectionStateKey, JSON.stringify(initialCols));
      } else {
        setCollectionColumns([]);
        localStorage.setItem(collectionStateKey, JSON.stringify([]));
      }
    }
    if (setAOVCollectionColumns && type === "aov") {
      if (initialCols?.length) {
        setAOVCollectionColumns(initialCols);
        localStorage.setItem(collectionStateKey, JSON.stringify(initialCols));
      } else {
        setAOVCollectionColumns([]);
        localStorage.setItem(collectionStateKey, JSON.stringify([]));
      }
    }
  };

  const handleExpand = (expanded: string[]) => {
    setExpand(expanded);
  };

  const renderList = (
    <CheckboxTree
      nodes={newGroup}
      checked={checked}
      expanded={expanded}
      icons={{
        check: <CheckBox style={{ color: "#00a0d1" }} />,
        uncheck: <CheckBoxOutlineBlank style={{ color: "#00a0d1" }} />,
        // expandOpen: <span className="ag-icon ag-icon-tree-open" />,
        // expandClose: <span className="ag-icon ag-icon-tree-closed" />,
        expandOpen: <ExpandMore />,
        expandClose: <ChevronRight />,
        halfCheck: <IndeterminateCheckBox style={{ color: "#00a0d1" }} />,
      }}
      showNodeTitles={true}
      showNodeIcon={false}
      onCheck={handleCheck}
      onExpand={handleExpand}
    />
  );

  return <List>{renderList}</List>;
});

export default CollectionRevenueCellRenderer;
